import { useEffect, useRef, useState, type FormEvent } from "react";
import { DeviceVoiceProfilePanel } from "./DeviceVoiceProfilePanel";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export type Enrollment = {
  enrollmentId: string;
  state: "prepared" | "fetched" | "revoked" | "expired";
  configUrl: string;
  expiresAt: number;
  lastFetchAt: number | null;
};
export type SetupStatus = {
  serverReady: boolean;
  publicReady: boolean;
  supported: boolean;
  version: number;
  serial: string;
  enrollment: Enrollment | null;
  callingReady: false;
  detail: string;
};
type Prepared = Enrollment & {
  bundle: {
    certificatePem: string;
    privateKeyPem: string;
    issuerCertificatePem: string;
  };
};
export const enrollmentMetadata = (value: Enrollment): Enrollment => ({
  enrollmentId: value.enrollmentId,
  state: value.state,
  configUrl: value.configUrl,
  expiresAt: value.expiresAt,
  lastFetchAt: value.lastFetchAt,
});
export const mayRetainSetupBundle = (
  requested: number,
  current: number,
  visible: boolean,
  focused: boolean,
) => requested === current && visible && focused;
type Props = {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  deviceId: string;
  deviceName: string;
  customer: boolean;
  readOnly?: boolean | undefined;
};

const date = (seconds: number) => new Date(seconds * 1000).toLocaleString();
export function ProvisioningServerStatus(props: {
  api: Api;
  customer: boolean;
}) {
  const [value, setValue] = useState<{
    serverReady: boolean;
    publicReady: boolean;
  } | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setValue(null);
    setError("");
    props
      .api<{ serverReady: boolean; publicReady: boolean }>(
        `/${props.customer ? "customer" : "admin"}/provisioning-status`,
        { signal: controller.signal },
      )
      .then((result) => {
        if (!controller.signal.aborted) setValue(result);
      })
      .catch((reason) => {
        if (!controller.signal.aborted)
          setError(
            reason instanceof Error
              ? reason.message
              : "Device setup status could not be loaded.",
          );
      });
    return () => controller.abort();
  }, [props.api, props.customer, revision]);
  return (
    <section className="cr-panel">
      <h3>Device setup</h3>
      {error ? (
        <p role="alert">{error}</p>
      ) : !value ? (
        <p role="status">Checking the setup server…</p>
      ) : (
        <>
          <p role="status">
            {value.serverReady
              ? value.publicReady
                ? "Setup server connected"
                : "Setup server running. Public connection is being completed."
              : "Setup server unavailable"}
          </p>
          <p>
            Add the equipment below, then open its Automatic setup section.
            Cache River supplies the setup files; you do not need a separate
            provider login.
          </p>
          {!value.publicReady && (
            <p className="cr-form-note">
              Keep using your existing phone settings until Cache River confirms
              that device setup is ready.
            </p>
          )}
        </>
      )}
      <button
        type="button"
        className="cr-button secondary"
        onClick={() => setRevision((current) => current + 1)}
      >
        Check setup server
      </button>
    </section>
  );
}
export function provisioningStatusText(value: SetupStatus) {
  if (!value.serverReady) return "Setup server unavailable";
  if (!value.publicReady)
    return "Setup server running; public connection pending";
  if (!value.supported) return "This model needs a setup profile";
  if (!value.enrollment) return "Ready to prepare device setup";
  if (value.enrollment.state === "revoked")
    return "Configuration access revoked";
  if (value.enrollment.state === "expired") return "Device certificate expired";
  return value.enrollment.lastFetchAt
    ? "Device contacted setup server"
    : "Waiting for the device";
}
export function DeviceSetupSummary({ value }: { value: SetupStatus }) {
  return (
    <>
      <p role="status">
        <strong>{provisioningStatusText(value)}</strong>
      </p>
      {!value.publicReady && (
        <p className="cr-form-note">
          Cache River must finish the public connection before you change the
          phone's settings. Your existing service is unaffected.
        </p>
      )}
      {!value.supported && (
        <p className="cr-form-note">
          Keep the exact model in the equipment record. Cache River must check
          its configuration format before setup is available.
        </p>
      )}
      {value.enrollment && (
        <dl className="cr-details">
          <div>
            <dt>Last authenticated setup request</dt>
            <dd>
              {value.enrollment.lastFetchAt
                ? date(value.enrollment.lastFetchAt)
                : "Not received"}
            </dd>
          </div>
          <div>
            <dt>Device certificate expires</dt>
            <dd>{date(value.enrollment.expiresAt)}</dd>
          </div>
        </dl>
      )}
      <p className="cr-form-note">
        Setup does not activate calling. Cache River must separately apply the
        phone account and verify its emergency address and routing.
      </p>
    </>
  );
}
function saveFile(name: string, content: string) {
  const url = URL.createObjectURL(
    new Blob([content], { type: "application/octet-stream" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function DeviceProvisioningPanel(props: Props) {
  const path = `/${props.customer ? "customer" : "admin"}/accounts/${props.accountId}/equipment/${props.deviceId}/provisioning`;
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [kit, setKit] = useState<Prepared | null>(null);
  const [revision, setRevision] = useState(0);
  const [revokeConfirmed, setRevokeConfirmed] = useState(false);
  const generation = useRef(0);
  const secretGeneration = useRef(0);
  const requestId = useRef(crypto.randomUUID());
  useEffect(() => {
    const hide = () => {
      ++secretGeneration.current;
      setKit(null);
    };
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    const visibility = () => {
      if (document.visibilityState !== "visible") hide();
    };
    document.addEventListener("visibilitychange", visibility);
    return () => {
      ++secretGeneration.current;
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);
  useEffect(() => {
    const current = ++generation.current;
    setKit(null);
    setError("");
    setStatus(null);
    setNotice("");
    setBusy(false);
    setRevokeConfirmed(false);
    requestId.current = crypto.randomUUID();
    if (!open) return;
    const controller = new AbortController();
    setBusy(true);
    props
      .api<SetupStatus>(path, { signal: controller.signal })
      .then((value) => {
        if (generation.current === current) setStatus(value);
      })
      .catch((reason) => {
        if (!controller.signal.aborted && generation.current === current)
          setError(
            reason instanceof Error
              ? reason.message
              : "Device setup could not be loaded.",
          );
      })
      .finally(() => {
        if (generation.current === current) setBusy(false);
      });
    return () => {
      ++generation.current;
      controller.abort();
    };
  }, [props.api, path, open, revision, props.readOnly]);
  useEffect(() => {
    if (!kit) return;
    const hide = () => setKit(null);
    const timer = setTimeout(hide, 60_000);
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
    };
  }, [kit]);
  async function prepare(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!status || props.customer || props.readOnly || busy) return;
    const current = generation.current;
    const secretRequest = secretGeneration.current;
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setKit(null);
    try {
      const result = await props.api<Prepared>(`${path}/prepare`, {
        method: "POST",
        csrf: props.csrf,
        body: {
          version: status.version,
          requestId: requestId.current,
          serial: data.get("serial"),
          hardwareRevision: data.get("hardwareRevision"),
          firmware: data.get("firmware"),
          confirmed: data.get("confirmed") === "on",
        },
      });
      if (generation.current !== current) return;
      const showBundle = mayRetainSetupBundle(
        secretRequest,
        secretGeneration.current,
        document.visibilityState === "visible",
        document.hasFocus(),
      );
      if (showBundle) setKit(result);
      setStatus({ ...status, enrollment: enrollmentMetadata(result) });
      setNotice(
        showBundle
          ? "Setup prepared. Save the device certificate now; its private key is shown only once."
          : "Setup was prepared while this window was hidden. Its private key was discarded. Revoke that setup before preparing a new certificate.",
      );
    } catch (reason) {
      if (generation.current === current)
        setError(
          `${reason instanceof Error ? reason.message : "Setup could not be prepared."} Refresh status before retrying; the server may have completed the request.`,
        );
    } finally {
      if (generation.current === current) setBusy(false);
    }
  }
  async function revoke() {
    if (
      !status?.enrollment ||
      !revokeConfirmed ||
      props.customer ||
      props.readOnly ||
      busy
    )
      return;
    const current = generation.current;
    setBusy(true);
    setError("");
    setKit(null);
    try {
      await props.api(`${path}/revoke`, {
        method: "POST",
        csrf: props.csrf,
        body: { enrollmentId: status.enrollment.enrollmentId, confirmed: true },
      });
      if (generation.current !== current) return;
      setStatus({
        ...status,
        enrollment: { ...status.enrollment, state: "revoked" },
      });
      requestId.current = crypto.randomUUID();
      setRevokeConfirmed(false);
      setNotice(
        "Further configuration downloads are blocked. Settings already on the phone were not erased.",
      );
    } catch (reason) {
      if (generation.current === current)
        setError(
          reason instanceof Error
            ? reason.message
            : "Revocation could not be confirmed. Refresh status.",
        );
    } finally {
      if (generation.current === current) setBusy(false);
    }
  }
  const canPrepare =
    status?.serverReady &&
    status.publicReady &&
    status.supported &&
    (!status.enrollment ||
      ["revoked", "expired"].includes(status.enrollment.state));
  return (
    <details
      className="cr-panel cr-editor"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>Automatic setup: {props.deviceName}</summary>
      {open && (
        <div className="cr-device-setup">
          {busy && <p role="status">Checking device setup…</p>}
          {error && (
            <p className="cr-notice error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="cr-notice" role="status">
              {notice}
            </p>
          )}
          {status && <DeviceSetupSummary value={status} />}
          {status && !props.customer && !props.readOnly && (
            <DeviceVoiceProfilePanel
              key={`${path}:${status.enrollment?.enrollmentId ?? "none"}:${status.enrollment?.state ?? "none"}`}
              api={props.api}
              path={path}
              csrf={props.csrf}
            />
          )}
          <button
            type="button"
            className="cr-button secondary"
            disabled={busy}
            onClick={() => setRevision((value) => value + 1)}
          >
            Refresh setup status
          </button>
          {props.readOnly && (
            <p className="cr-form-note">
              Exit customer preview to prepare or revoke equipment. Device keys
              are never shown in preview.
            </p>
          )}
          {props.customer && (
            <p className="cr-form-note">
              Cache River prepares and approves your device. You do not need a
              provider account or a SIP password to request setup.
            </p>
          )}
          {!props.customer && !props.readOnly && canPrepare && (
            <form onSubmit={prepare}>
              <h4>Prepare this device</h4>
              <div className="cr-form-grid">
                <label className="cr-field">
                  <span>Serial number from its label</span>
                  <input
                    name="serial"
                    required
                    maxLength={120}
                    defaultValue={status.serial}
                  />
                </label>
                <label className="cr-field">
                  <span>Hardware revision</span>
                  <input name="hardwareRevision" required maxLength={120} />
                </label>
                <label className="cr-field">
                  <span>Current firmware version</span>
                  <input name="firmware" required maxLength={120} />
                </label>
              </div>
              <p className="cr-form-note">
                Read the current version from the device. Do not reset or
                upgrade it. The first physical device still needs a controlled
                setup test before customer use.
              </p>
              <label className="cr-check">
                <input name="confirmed" type="checkbox" required /> I checked
                this device's model and MAC address. I will install these files
                only during an agreed setup window.
              </label>
              <button className="cr-button" disabled={busy}>
                Prepare setup files
              </button>
            </form>
          )}
          {kit && !props.customer && !props.readOnly && (
            <section>
              <h4>Save this device's certificate</h4>
              <p>
                This file contains a private key. Keep it with the authorized
                installer, never in email or a support ticket. It hides after
                one minute or when this window loses focus.
              </p>
              <button
                className="cr-button"
                type="button"
                onClick={() =>
                  saveFile(
                    "cache-river-device-client.pem",
                    `${kit.bundle.privateKeyPem}\n${kit.bundle.certificatePem}\n${kit.bundle.issuerCertificatePem}`,
                  )
                }
              >
                Save device certificate
              </button>
              <p className="cr-form-note">
                During controlled setup, install this as the device's custom
                client certificate and use HTTPS configuration. Confirm the
                first authenticated download here before continuing. Do not
                change existing SIP accounts or turn off certificate validation.
              </p>
              <label className="cr-field">
                <span>Configuration server address</span>
                <input
                  readOnly
                  value={new URL(kit.configUrl).origin}
                  onFocus={(event) => event.currentTarget.select()}
                />
              </label>
              <button
                className="cr-button secondary"
                type="button"
                onClick={() => setKit(null)}
              >
                Hide setup files
              </button>
            </section>
          )}
          {!props.customer &&
            !props.readOnly &&
            status?.enrollment &&
            !["revoked", "expired"].includes(status.enrollment.state) && (
              <section>
                <h4>Revoke configuration access</h4>
                <p>
                  Stops future downloads for this device. This does not erase
                  settings already installed or stop its current calls.
                </p>
                <label className="cr-check">
                  <input
                    type="checkbox"
                    checked={revokeConfirmed}
                    onChange={(event) =>
                      setRevokeConfirmed(event.target.checked)
                    }
                  />{" "}
                  Revoke this device's setup certificate
                </label>
                <button
                  type="button"
                  className="cr-button secondary"
                  disabled={busy || !revokeConfirmed}
                  onClick={revoke}
                >
                  Revoke setup access
                </button>
              </section>
            )}
        </div>
      )}
    </details>
  );
}
